"use client";

import { Logo } from "@/components/logo/Logo";
import { LogoIcon } from "@/components/logo/LogoIcon";
import { SOCIAL_ICONS } from "@/components/ui/SocialIcons";
import { publicUrl } from "@/lib/content/path";
import { usePublicSettings } from "@/lib/use-public-settings";

const COLUMNS = [
  {
    heading: "Top Treatments",
    links: [
      "Hair Transplant Cost In Delhi",
      "Unshaven FUE Hair Transplant",
      "Beard Hair Transplant",
      "Laser Hair Reduction",
      "Laser Tattoo Removal",
      "Acne Treatment",
    ],
  },
  {
    heading: "Quick Links",
    links: [
      "Success Story",
      "Gallery",
      "International Patient",
      "Contact us",
      "FAQ",
      "Blog",
    ],
  },
  {
    heading: "Legal",
    links: ["Privacy Policy", "Terms & Conditions", "Disclaimer"],
  },
];

const COPYRIGHT_YEAR = 2026;

export function Footer() {
  const { contact, social } = usePublicSettings();
  return (
    <footer
      id="contact"
      className="relative bg-brand-black text-white overflow-hidden content-visibility-auto"
      style={{ containIntrinsicSize: "auto none auto 500px" }}
    >
      <div className="absolute inset-0 brand-cell-pattern opacity-[0.08]" />
      
      {/* Background Logo Icon */}
      <div className="absolute bottom-0 right-0 -mr-32 -mb-32 opacity-10 pointer-events-none rotate-[15deg]">
        <LogoIcon className="w-[600px] h-auto text-brand-orange" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 pt-16 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_repeat(3,1fr)] gap-12">
          <div>
            <div className="mb-2 w-fit rounded-2xl bg-white px-4 py-3"><Logo variant="white" className="h-10 lg:h-12 w-auto" /></div>
            <p className="mt-5 max-w-xs text-sm text-white/60 leading-relaxed">
              Advanced hair restoration and skin rejuvenation, trusted by patients across
              20+ countries.
            </p>
            <div className="mt-6 flex gap-3">
              {social.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 hover:bg-brand-orange transition-colors"
                  aria-label={s.label}
                >
                  {SOCIAL_ICONS[s.label]}
                </a>
              ))}
            </div>
          </div>

          {COLUMNS.map((col) => (
            <div key={col.heading}>
              <span className="block font-accent text-xs text-brand-peach uppercase mb-4">
                {col.heading}
              </span>
              <ul className="space-y-2.5">
                {col.links.map((link) => {
                  const routes: Record<string, string> = {
                    "Hair Transplant Cost In Delhi": "/hair-transplant-cost-in-delhi",
                    "Unshaven FUE Hair Transplant": "/long-hair-transplant",
                    "Beard Hair Transplant": "/hair-transplant/beard-hair-transplant",
                    "Laser Hair Reduction": "/laser/laser-hair-removal",
                    "Laser Tattoo Removal": "/laser/tattoo-removal-in-delhi",
                    "Acne Treatment": "/acne-treatment",
                    "Success Story": "/success-stories",
                    "Gallery": "/results/male-hair-transplant",
                    "International Patient": "/global-client",
                    "Contact us": "/contact-us",
                    "FAQ": "/hair-transplant-faqs",
                    "Blog": "/blog/",
                    "Privacy Policy": "/privacy-policy",
                    "Terms & Conditions": "/terms-and-conditions",
                    "Disclaimer": "/disclaimer",
                  };
                  const href = publicUrl(routes[link] || "/");
                  
                  return (
                    <li key={link}>
                      <a
                        href={href}
                        className="text-sm text-white/70 hover:text-white transition-colors"
                      >
                        {link}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-6 rounded-2xl bg-white/5 p-6">
          <div>
            <p className="font-accent text-[11px] text-white/50 uppercase mb-1">Visit</p>
            <p className="text-sm text-white/85">{contact.address}</p>
          </div>
          <div>
            <p className="font-accent text-[11px] text-white/50 uppercase mb-1">Call</p>
            <a href={contact.phoneHref} className="text-sm text-white/85 hover:text-brand-orange">
              {contact.phone}
            </a>
          </div>
          <div>
            <p className="font-accent text-[11px] text-white/50 uppercase mb-1">Email</p>
            <a
              href={`mailto:${contact.email}`}
              className="text-sm text-white/85 hover:text-brand-orange"
            >
              {contact.email}
            </a>
          </div>
        </div>

        <p className="mt-10 text-xs text-white/40 leading-relaxed max-w-3xl">
          Disclaimer: The results shown on this website are for educational purposes and to
          spread awareness. Results vary from patient to patient — Dermalife does not
          guarantee identical outcomes for every individual.
        </p>

        <div className="mt-6 flex flex-col sm:flex-row justify-between gap-3 border-t border-white/10 pt-6 text-xs text-white/40">
          <p>© {COPYRIGHT_YEAR} Dermalife. All rights reserved.</p>
          <a
            href="https://www.techasoft.com"
            target="_blank"
            rel="noopener noreferrer"
            className="font-accent uppercase tracking-wide transition-colors hover:text-brand-orange"
          >
            Designed &amp; Developed by Techasoft Pvt. Ltd
          </a>
        </div>
      </div>
    </footer>
  );
}
